import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { dataConstants } from '../constants';
import useAuth from './auth';
import _getWideScreenerMetrics from '../../lib/screener/_getScreenerMetrics';
import _getWideScreenerMetricsCount from '../../lib/screener/_getScreenerMetricsCount';
import _getScreens from '../../lib/screener/userOperations/_getScreens';
import _updateScreenerPreset from '../../lib/screener/userOperations/_updateScreen';
import _renameScreenerPreset from '../../lib/screener/userOperations/_renameScreen';
import _deleteScreenerPreset from '../../lib/screener/userOperations/_deleteScreen';
import getWatchlist from '../../lib/watchlist/_getWatchlist';
import { getFormula } from '../components/Screener/QueryBuilder/typeahead.util';
import { downloadCSV } from '../utils/helpers';
import * as send from '../../lib/analytics/send';

const { SCREENER_OPTIONS, DEFAULT_TBL_COLS } = dataConstants;
const sortedOptions = [...SCREENER_OPTIONS].sort((a, b) => b.shortName.length - a.shortName.length);

// ─── Shared constants ────────────────────────────────────────────

export const options = SCREENER_OPTIONS;
export { DEFAULT_TBL_COLS };

export const mainHeadCols = [
  { "name": "", "colSpan": "3", "includeCols": ["ticker", "shortName"] },
  { "name": "Fundamentals", "colSpan": "2", "includeCols": ["MKTCAP", "REV_TTM"] },
  { "name": "Returns", "colSpan": "2", "includeCols": ["PCHG__180"] },
  { "name": "Valuation", "colSpan": "3", "includeCols": ["PE", "PS", "MKTEBT"] },
  { "name": "Growth", "colSpan": "3", "includeCols": ["ROE", "REV_TTM__PCHG_1Y", "NETINC_TTM__PCHG_1Y"] },
  { "name": "Income Statement", "colSpan": "0", "includeCols": [] },
  { "name": "Balance Sheet", "colSpan": "0", "includeCols": [] },
  { "name": "Cashflow Statement", "colSpan": "0", "includeCols": [] },
  { "name": "Statistics", "colSpan": "0", "includeCols": [] },
  { "name": "Operations", "colSpan": "0", "includeCols": [] },
  { "name": "", "colSpan": "0", "includeCols": ["delete"] }
];

export const defaultCols = [
  { "label": "Fundamentals", "name": "Ticker", "tooltip": "Ticker", "field": "ticker", "urlField": "ticker", "class": "text-left", sortFunc: (a, b) => a.localeCompare(b) },
  { "label": "Fundamentals", "name": "Name", "tooltip": "Name", "field": "tickername", "class": "td-color", "urlField": "ticker" },
  { "label": "Fundamentals", "name": "Sector", "field": "sector", "class": "text-left", "isSectorValue": false, "isPct": false, "mult": 1, "tooltip": "Sector" },
  { "label": "Fundamentals", "name": "Market Cap", "field": "MKTCAP", "class": "sorted", "isSectorValue": true, "isPct": false, "mult": 1, "tooltip": "Market Capital" },
  { "label": "Income Statement", "name": "Revenues TTM", "field": "REV_TTM", "class": "text-right", "isSectorValue": true, "isPct": false, "mult": 1, "tooltip": "Net sales" },
  { "label": "Returns", "name": "1W Pct", "field": "PCHG__7", "class": "text-right", "isSectorValue": false, "isPct": true, "mult": 100, "tooltip": "1-Week Stock Price Percent Change" },
  { "label": "Returns", "name": "1M Pct", "field": "PCHG__30", "class": "text-right", "isSectorValue": false, "isPct": true, "mult": 100, "tooltip": "1-Month Stock Price Percent Change" },
  { "label": "Returns", "name": "1Y Pct", "field": "PCHG__365", "class": "text-right", "isSectorValue": false, "isPct": true, "mult": 100, "tooltip": "1-Year Percent Change in Stock Price" },
  { "label": "Valuation", "name": "PE", "field": "PE", "class": "text-right", "isSectorValue": false, "isPct": false, "mult": 1, "tooltip": "Price to Earnings Ratio" },
  { "label": "Valuation", "name": "PS", "field": "PS", "class": "text-right", "isSectorValue": false, "isPct": false, "mult": 1, "tooltip": "Price to Sales Ratio" },
  { "label": "Valuation", "name": "ROE", "field": "ROE", "class": "text-right", "isSectorValue": false, "isPct": true, "mult": 1, "tooltip": "Return on Equity" },
  { "label": "Growth", "name": "Rev 3Y Grw", "field": "REV_TTM__PCHG_3Y", "class": "text-right", "isSectorValue": false, "isPct": true, "mult": 1, "tooltip": "Reveue Grw in past 3-Years" },
  { "label": "Returns", "name": "Drawdowns", "field": "DRAWDN", "class": "text-right", "isSectorValue": false, "isPct": true, "mult": 1, "tooltip": "DrawDown" }
];

// ─── Helpers ─────────────────────────────────────────────────────

export const parseCriteria = (criteria) => {
  if (!criteria) return null;
  return criteria
    .replace(/[()"]/g, '')
    .replace(/AND/g, 'AND\n')
    .replace(/ and /g, ' AND\n')
    .replace(/ or /g, ' OR ');
};

export const aggCodeToShortName = (text) => {
  if (!text) return '';
  return text.replace(/`([^`]+)`/g, (match, p1) => {
    const replacement = SCREENER_OPTIONS.find(item => item.aggCode === p1);
    return replacement ? replacement.shortName : match;
  });
};

export const parseTableColumns = (columnsStr) => {
  if (!columnsStr) return defaultCols;
  try {
    return JSON.parse(columnsStr.replace(/'/g, '"')).map(
      field => DEFAULT_TBL_COLS.find(item => item?.field === field)
    );
  } catch {
    return defaultCols;
  }
};

export const userHasPresets = (array) => {
  if (!array) return false;
  for (const obj of array) {
    if (!obj.isSystemDefault) return true;
  }
  return false;
};

// ─── Hook ────────────────────────────────────────────────────────

/**
 * Shared screener state hook used by all 3 screener pages.
 *
 * @param {Object} config
 * @param {Array}  config.screens         – Pre-loaded screens list from getStaticProps
 * @param {Array}  config.initialMetrics  – SSR screener rows (preset mode only)
 * @param {number} config.initialCount    – SSR result count (preset mode only)
 * @param {Array}  config.initialFormula  – SSR parsed formula (preset mode only)
 * @param {string} config.initialCriteria – Raw criteria string (preset mode only)
 * @param {string} config.initialPresetName
 * @param {string} config.initialPresetDesc
 * @param {string} config.initialPresetCode
 * @param {string} config.initialTableColumns – JSON-ish column string (preset mode only)
 * @param {'index'|'preset'|'user-preset'} config.mode
 */
export default function useScreenerState({
  screens = [],
  initialMetrics = null,
  initialCount = null,
  initialFormula = null,
  initialCriteria = null,
  initialPresetName = null,
  initialPresetDesc = null,
  initialPresetCode = null,
  initialTableColumns = null,
  mode = 'index',
} = {}) {
  const router = useRouter();
  const { user, logout } = useAuth();
  const { reloadUserInfo } = user || {};
  const isPremium = reloadUserInfo?.customAttributes
    && (JSON.parse(reloadUserInfo.customAttributes)?.userType === "PREMIUM"
      || JSON.parse(reloadUserInfo.customAttributes)?.userType === "PROFESSIONAL")
    ? true : false;

  // ─── Data state ──────────────────────────────────────────────
  const [screenerMetrics, setScreenerMetrics] = useState(initialMetrics ?? []);
  const [estimatedResults, setEstimatedResults] = useState(initialCount ?? null);
  const [showingResults, setShowingResults] = useState(initialMetrics?.length ?? undefined);
  const [formula, setFormula] = useState(initialFormula ?? []);
  const [andor, setAndor] = useState('AND');
  const [inputText, setInputText] = useState(
    initialCriteria ? aggCodeToShortName(parseCriteria(initialCriteria)) : ''
  );

  // ─── Preset state ───────────────────────────────────────────
  const [userPresets, setUserPresets] = useState(screens ?? []);
  const [userUpdatedPreset, setUserUpdatedPreset] = useState(null);
  const [userDeletedPreset, setUserDeletedPreset] = useState(null);
  const [activePresetName, setActivePresetName] = useState(initialPresetName ?? '');
  const [activePresetDesc, setActivePresetDesc] = useState(initialPresetDesc ?? '');
  const [activePresetCode, setActivePresetCode] = useState(initialPresetCode ?? '');
  const [presetCriteria, setPresetCriteria] = useState(
    initialCriteria ? parseCriteria(initialCriteria) : null
  );

  // ─── Table state ────────────────────────────────────────────
  const [tableCols, setTableCols] = useState(parseTableColumns(initialTableColumns));
  const [columnGroups, setColumnGroups] = useState(mainHeadCols);
  const [first, setFirst] = useState(100);
  const [offset, setOffset] = useState(0);

  // ─── UI state ───────────────────────────────────────────────
  const [isLoading, setLoading] = useState(mode === 'user-preset');
  const [isTableLoading, setTableLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [banner, setBanner] = useState(false);
  const [renameDialog, setRenameDialog] = useState(false);

  // ─── Mode-specific state ────────────────────────────────────
  const [screenerNotFound, setScreenerNotFound] = useState(false);
  const [watchlists, setWatchlists] = useState([]);

  // ─── Handlers ───────────────────────────────────────────────

  const logoutUser = () => {
    logout();
    router.push({ pathname: '/auth', query: { from: router.asPath } });
  };

  const handleRenameOpen = () => {
    if (isPremium) {
      setRenameDialog(prev => !prev);
    } else {
      setBanner(true);
    }
  };

  const handleRenameClose = () => setRenameDialog(false);
  const handleBannerClose = () => setBanner(false);

  const handleExited = () => {
    setUserUpdatedPreset(null);
    setUserDeletedPreset(null);
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setTableCols(prevValue => {
      const newValue = [...prevValue];
      const [itemToMove] = newValue.splice(oldIndex, 1);
      newValue.splice(newIndex, 0, itemToMove);
      return newValue;
    });
  };

  const getScreenersCount = async (_andor, _formula) => {
    if (_formula?.length > 0) {
      setLoading(true);
      await _getWideScreenerMetricsCount(_andor, _formula).then((d) => {
        setEstimatedResults(d?.getWideScreenerMetricsCountV3);
        setLoading(false);
      }).catch((error) => {
        console.log(error);
        setLoading(false);
      });
    }
  };

  const getScreeners = async (_andor, _formula) => {
    if (_formula?.length > 0) {
      setTableLoading(true);
      await _getWideScreenerMetrics(_andor, _formula, first, offset).then((d) => {
        setShowingResults((showingResults ?? 0) + (d?.getWideScreenerMetricsV3?.length ?? 0));
        offset > 0
          ? setScreenerMetrics(d?.message?.message ? { err: d?.message } : [...screenerMetrics, ...d?.getWideScreenerMetricsV3])
          : setScreenerMetrics(d?.message?.message ? { err: d?.message } : d?.getWideScreenerMetricsV3);
        setTableLoading(false);
      }).catch(() => {
        setTableLoading(false);
      });
    }
  };

  const handleNewMetrics = (_formula) => {
    getScreeners('AND', _formula);
    getScreenersCount('AND', _formula);
  };

  const runQuery = (text) => {
    setInputText(text);
    let converted = text;
    for (const entry of sortedOptions) {
      const regex = new RegExp('\\b' + entry.shortName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'gi');
      converted = converted.replace(regex, '`' + entry.aggCode + '`');
    }
    const _formula = getFormula(converted, sortedOptions);
    setFormula(_formula);
  };

  const handleDownload = (e) => {
    if (isPremium) {
      downloadCSV(
        screenerMetrics?.sort((a, b) => b?.MKTCAP - a?.MKTCAP),
        tableCols,
        `Screener Table`,
        true
      );
      send.uevent(e);
    } else {
      setBanner(true);
    }
  };

  const updatePreset = (screen_name, screen_desc) => {
    let textarea = typeof window !== 'undefined' && document?.getElementById('query-builder');
    let input_text = textarea?.value;
    let columns = JSON.stringify(
      tableCols?.filter(item => item !== null && item !== undefined)?.map(item => item?.field)
    ).replace(/"/g, "'");

    if (!user?.accessToken) throw new Error('Missing user access token');
    if (!screen_name) throw new Error('Missing preset name');

    if (input_text !== "") {
      setSaveLoading(true);
      for (var i = 0; i < sortedOptions.length; i++) {
        var entry = sortedOptions[i];
        var shortName = entry.shortName;
        var aggCode = entry.aggCode;
        var regex = new RegExp("\\b" + shortName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + "\\b", "gi");
        input_text = input_text.replace(regex, "`" + aggCode + "`");
      }
      _updateScreenerPreset(screen_name, screen_desc, andor, getFormula(input_text, sortedOptions), input_text, columns, user?.accessToken)
        .then(response => {
          setUserUpdatedPreset(response?.updateScreen?.screen_name);
          setSaveLoading(false);
        })
        .catch(error => {
          console.error('Error updating preset:', error);
          alert('An error occurred while updating the preset. Please try again later.');
          setSaveLoading(false);
        });
    }
  };

  const renamePreset = (screen_name, screen_desc, code) => {
    if (!user?.accessToken) throw new Error('Missing user access token');
    if (!screen_name) throw new Error('Missing preset name');

    _renameScreenerPreset(screen_name, screen_desc, code, user?.accessToken)
      .then(response => {
        setUserUpdatedPreset(response?.renameScreen?.screen_name);
        setActivePresetName(response?.renameScreen?.screen_name && activePresetName === screen_name ? response?.renameScreen?.screen_name : screen_name);
        setActivePresetDesc(screen_desc);
      })
      .catch(error => {
        console.error('Error renaming preset:', error);
        alert('An error occurred while renaming the preset. Please try again later.');
      });
  };

  const deletePreset = async (screen_code) => {
    try {
      if (!user?.accessToken || !screen_code) throw new Error('Missing user access token or preset name');
      const response = await _deleteScreenerPreset(screen_code, user.accessToken);
      setUserDeletedPreset(response?.deleteScreenerPreset?.status);
      if (user?.accessToken) {
        _getScreens(user.accessToken).then(data => setUserPresets(data?.getScreens));
      }
    } catch (error) {
      console.error('Error deleting preset:', error);
      alert('An error occurred while deleting the preset. Please try again later.');
    }
  };

  const handleNewScreen = (e) => {
    if (mode !== 'index') {
      router.push('/stock-screener#build-query');
    }
    setActivePresetName(''); setActivePresetDesc(''); setActivePresetCode('');
    setPresetCriteria(null); setScreenerMetrics([]); setEstimatedResults(null);
    setTableLoading(false); setLoading(false); setTableCols(defaultCols);
    send.uevent(e);
  };

  const tryThisQuery = async (criteria) => {
    if (!criteria || criteria === '') return;
    setLoading(true);
    setTableLoading(true);

    let processed = criteria;
    for (let i = 0; i < sortedOptions.length; i++) {
      const entry = sortedOptions[i];
      const regex = new RegExp("\\b" + entry.shortName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + "\\b", "gi");
      processed = processed.replace(regex, "`" + entry.aggCode + "`");
    }
    const _formula = getFormula(processed, sortedOptions) || [];

    setActivePresetName(router.query.screen ?? '');
    setActivePresetDesc('');
    setActivePresetCode('');
    setPresetCriteria(parseCriteria(criteria));
    setInputText(aggCodeToShortName(parseCriteria(criteria)));
    setTableCols(defaultCols);
    setOffset(0);
    setFormula(_formula);

    const { getWideScreenerMetricsCountV3 } = await _getWideScreenerMetricsCount(andor, _formula) || {};
    setEstimatedResults(getWideScreenerMetricsCountV3);
    setShowingResults(getWideScreenerMetricsCountV3);
    setLoading(false);

    const { getWideScreenerMetricsV3 } = await _getWideScreenerMetrics(andor, _formula, first, offset) || {};
    setScreenerMetrics(getWideScreenerMetricsV3);
    setTableLoading(false);
  };

  // ─── Effects ────────────────────────────────────────────────

  // Sync showingResults from screenerMetrics
  useEffect(() => {
    setShowingResults(screenerMetrics?.length);
  }, [screenerMetrics]);

  // Refresh user presets when auth or preset mutations change
  useEffect(() => {
    if (mode === 'user-preset') return; // user-preset handles this in its auth effect
    if (user?.accessToken) {
      _getScreens(user.accessToken).then(data => setUserPresets(data?.getScreens ?? []));
    } else {
      _getScreens('').then(data => setUserPresets(data?.getScreens ?? []));
    }
  }, [user, userUpdatedPreset, userDeletedPreset]);

  // Init presets from props on mount (no user)
  useEffect(() => {
    if (mode === 'user-preset') return;
    if (!user?.accessToken) setUserPresets(screens ?? []);
  }, []);

  // Fetch data when formula/offset/first changes
  useEffect(() => {
    if (mode === 'preset' && initialMetrics && formula === initialFormula) {
      // Skip initial fetch for preset mode — SSR data already loaded
      return;
    }
    if (formula?.length > 0) {
      getScreeners('AND', formula);
      getScreenersCount('AND', formula);
    }
  }, [formula, first, offset]);

  // Sync inputText from presetCriteria changes (preset selection)
  useEffect(() => {
    if (mode === 'user-preset') return; // user-preset sets inputText directly
    if (presetCriteria) {
      setInputText(aggCodeToShortName(presetCriteria));
    } else {
      setInputText('');
    }
  }, [presetCriteria]);

  // Preset mode: re-sync state when SSR props change (navigating between presets)
  useEffect(() => {
    if (mode !== 'preset') return;
    if (initialMetrics) {
      setScreenerMetrics(initialMetrics);
      setEstimatedResults(initialCount);
      setShowingResults(initialMetrics?.length);
      setPresetCriteria(parseCriteria(initialCriteria));
      setInputText(initialCriteria ? aggCodeToShortName(parseCriteria(initialCriteria)) : '');
      setTableCols(parseTableColumns(initialTableColumns));
      setActivePresetName(initialPresetName ?? '');
      setActivePresetDesc(initialPresetDesc ?? '');
      setActivePresetCode(initialPresetCode ?? '');
    }
  }, [initialMetrics]);

  // User-preset mode: client-side fetch preset data after auth
  useEffect(() => {
    if (mode !== 'user-preset') return;
    if (!user?.accessToken) return;
    setLoading(true);
    _getScreens(user.accessToken).then(async (data) => {
      setUserPresets(data?.getScreens ?? []);
      const u_screen = data?.getScreens?.find(d => d.code === router.query.screen);
      setScreenerNotFound(!u_screen?.code);

      setActivePresetName(u_screen?.screen_name ?? router.query.name ?? '');
      setActivePresetDesc(u_screen?.screen_desc ?? '');
      setActivePresetCode(u_screen?.code ?? router.query.screen ?? '');

      const criteria = u_screen?.criteria;
      setPresetCriteria(parseCriteria(criteria));
      setInputText(criteria
        ? aggCodeToShortName(parseCriteria(criteria))
        : router.query.query
          ? aggCodeToShortName(parseCriteria(router.query.query))
          : '');
      setTableCols(u_screen?.columns ? parseTableColumns(u_screen.columns) : defaultCols);

      // Compute formula and fetch data directly (avoids double-fetch from formula effect)
      const _formula = getFormula(parseCriteria(criteria), sortedOptions) || [];
      setFormula(_formula); // Store formula for pagination
      if (_formula.length > 0) {
        const { getWideScreenerMetricsV3 } = await _getWideScreenerMetrics(andor, _formula, first, offset) || {};
        const { getWideScreenerMetricsCountV3 } = await _getWideScreenerMetricsCount(andor, _formula) || {};
        setShowingResults(getWideScreenerMetricsCountV3);
        setEstimatedResults(getWideScreenerMetricsCountV3);
        setScreenerMetrics(getWideScreenerMetricsV3);
      }
      setLoading(false);
    });
  }, [user, userUpdatedPreset, userDeletedPreset]);

  // Fetch watchlists (preset + user-preset modes)
  useEffect(() => {
    if (mode === 'index') return;
    if (user?.accessToken) {
      getWatchlist(user.accessToken).then(data => {
        setWatchlists(data?.getWatchlist ?? []);
      });
    }
  }, [user?.accessToken]);

  // ─── Return ─────────────────────────────────────────────────

  return {
    // Auth
    user, isPremium,
    // Data
    screenerMetrics, estimatedResults, showingResults, formula, andor, inputText,
    // Presets
    userPresets, userUpdatedPreset, userDeletedPreset,
    activePresetName, activePresetDesc, activePresetCode,
    presetCriteria,
    // Table
    tableCols, columnGroups, first, offset,
    // UI
    isLoading, isTableLoading, saveLoading, message, banner, renameDialog,
    // Mode-specific
    screenerNotFound, watchlists,
    // Setters
    setFormula, setTableCols, setPresetCriteria,
    setActivePresetName, setActivePresetDesc, setActivePresetCode,
    setBanner, setOffset, setScreenerMetrics, setEstimatedResults,
    setTableLoading, setLoading, setUserUpdatedPreset, setUserDeletedPreset,
    setColumnGroups, setScreenerNotFound,
    // Handlers
    getScreeners, getScreenersCount, handleNewMetrics, runQuery,
    updatePreset, renamePreset, deletePreset,
    handleDownload, onSortEnd,
    handleRenameOpen, handleRenameClose, handleBannerClose, handleExited,
    logoutUser, handleNewScreen, tryThisQuery, setInputText,
  };
}
